<script setup>
import {
  computed,
  reactive,
  watch,
  inject,
  onBeforeUnmount,
  onMounted,
} from "vue";
import { createReusableTemplate } from "@vueuse/core";
const [DefineTemplate, ReuseTemplate] = createReusableTemplate();
import { cn } from "@/lib/utils.js";
import { Link, usePage } from "@inertiajs/vue3";
import EventCard from "@/Components/EventCard.vue";
import { watchDebounced } from "@vueuse/core";
import axios from "axios";
import CloseIcon from "@/Components/svg/CloseIcon.vue";
const route = inject("route");

const props = defineProps({
  enabled: {
    type: Boolean,
    default: false,
  },
});

watch(
  () => props.enabled,
  (value) => {
    if (value) {
      document.body.style.overflow = "hidden";
      // add class search-open to body
      document.body.classList.add("search-open");
    } else {
      document.body.style.overflow = "auto";
      // remove class search-open from body
      document.body.classList.remove("search-open");
    }
  },
);

const emits = defineEmits(["escapeKeyDown"]);

// if props.enabled is true, when the esc key is pressed, set props.enabled to false
const escKey = (event) => {
  if (event.key === "Escape" && props.enabled) {
    console.log("Escape key pressed");
    emits("escapeKeyDown", false);
  }
};

onMounted(() => {
  document.addEventListener("keydown", escKey);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", escKey);
});

const {
  props: {
    siteSettings: { search_image, membership_page, newsletter_category },
    searchPayload,
  },
} = usePage();

const state = reactive({
  query: "",
  results: null,
  suggested: {
    event: null,
    news: null,
  },
  minH: 0,
  maxH: null,
});

watch(
  () => props.enabled,
  (enabled) => {
    if (!enabled) {
      state.query = "";
      state.results = null;
    }

    state.suggested = searchPayload;
  },
);

watchDebounced(
  () => state.query,
  (value) => {
    // if the new query is empty, reset the results
    if (!value || value.length === 0) {
      state.results = null;
      return;
    }

    axios
      .post(
        route("search.query", {
          query: value,
          category: newsletter_category.id,
        }),
      )
      .then(({ data, status }) => {
        if (status === 200) {
          state.results = data;
        }
      })
      .catch((error) => {
        console.error(error);
      });
  },
  { debounce: 700 },
);

const mergedResults = computed(() => {
  if (!state.results) {
    return [];
  }

  const results = [];
  const { news, events } = state.results;

  for (let i = 0; i < Math.max(news.length, events.length); i++) {
    if (news[i]) {
      results.push(news[i]);
    }

    if (events[i]) {
      results.push(events[i]);
    }
  }

  return results;
});
</script>

<template>
  <DefineTemplate v-slot="{ data }">
    <div>
      <div
        class="group inner-group relative box-border before:content-[''] before:block before:pt-[87%] w-full mb-4 last:mb-0 pb-4 md:mb-4 md:pb-0 lg:mb-0"
      >
        <a
          :href="data.url"
          class="h-auto news-a w-full box-border px-3 lg:px-6 absolute top-0 left-0 aspect-[1/0.87]"
        >
          <img
            :src="data.image"
            :alt="data.name"
            class="object-cover w-full h-full"
          />
        </a>
      </div>
      <div class="inner-tittle px-3 pt-2 lg:px-6 lg:pt-6">
        <h5 class="uppercase">{{ data.name }}</h5>
      </div>
    </div>
  </DefineTemplate>

  <div
    class="w-full fixed top-0 left-0 z-[-10] transition-all duration-300"
    :class="
      cn(
        { hidden: !enabled },
        { 'bg-brand-light ': enabled },
        enabled ? 'h-full' : 'h-0',
      )
    "
  >
    <form class="w-full mx-auto table h-full">
      <div class="table mt-36 lg:mt-64 w-full mx-auto">
        <div class="flex justify-center items-center space-x-2 w-full">
          <div class="table w-full relative border-b-brand-blue border-b">
            <input
              type="text"
              v-model="state.query"
              placeholder="Search by typing here"
              @keydown.enter.prevent
              class="w-full h3 focus-visible:ring-0 px-3 lg:px-6 z-10 mb-5 focus-visible:border-0 focus-visible:outline-none placeholder:text-brand-dark-blue/50 focus:ring-0 focus:border-0 border-0 rounded-none bg-transparent"
            />
            <button
              v-if="state.query || state.query.length"
              type="button"
              @click="
                () => {
                  state.query = '';
                  state.results = null;
                }
              "
              class="group absolute hover:cursor-pointer z-20 flex right-3 lg:right-6 bottom-[49%]"
            >
              <span
                class="uppercase font-sans leading-none select-none !text-brand-dark-blue/50 pr-2"
                >Clear</span
              >
              <CloseIcon class="w-[15px] h-[16px] text-brand-dark-blue" />
            </button>
          </div>
        </div>
        <div
          class="relative w-full h-full overflow-y-auto max-h-[calc(100dvh-192px)] lg:max-h-[calc(100dvh-322px)]"
        >
          <!-- Search Results -->
          <div v-if="mergedResults.length" class="w-full">
            <h6
              class="uppercase !text-brand-blue lg:px-6 px-3 pt-8 pb-3 select-none"
            >
              {{ mergedResults.length }} matches found
            </h6>
            <div
              class="group w-full mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mobile-items results"
            >
              <template v-for="item in mergedResults">
                <ReuseTemplate
                  class="result-item"
                  v-if="item.item_type === 'news'"
                  :data="item"
                />
                <div v-if="item.item_type === 'event'" class="mb-6 result-item">
                  <EventCard :event="item" />
                  <div class="px-3 lg:px-6 pt-3 lg:pt-6">
                    <h5 class="uppercase">{{ item.title }}</h5>
                  </div>
                </div>
              </template>
            </div>
          </div>
          <!-- Suggested Searches -->
          <div class="w-full">
            <h6
              class="uppercase pt-8 !text-brand-dark-blue/50 select-none lg:px-6 px-3"
            >
              Suggested searches
            </h6>
            <!-- Mobile Carousel -->
            <div class="mobile-carousel w-full max-w-[100vw] lg:hidden">
              <div class="flex overflow-x-auto">
                <div
                  v-if="state.suggested.news"
                  class="flex-shrink-0 w-[361px]"
                >
                  <ReuseTemplate class="" :data="state.suggested.news" />
                </div>
                <div
                  v-if="state.suggested.event"
                  class="flex-shrink-0 w-[361px]"
                >
                  <EventCard
                    class="border-l border-l-brand-dark-blue"
                    :event="state.suggested.event"
                  />
                  <div class="px-3 lg:px-6 pt-4 lg:pt-6">
                    <h5 class="uppercase">Latest Events</h5>
                  </div>
                </div>
                <div class="flex-shrink-0 w-[361px]">
                  <div
                    class="group relative box-border before:content-[''] before:block before:pt-[87%] w-full mb-4 last:mb-0 pb-0 md:mb-4 md:pb-0 lg:mb-0 border-l border-l-brand-dark-blue"
                  >
                    <Link
                      :href="membership_page"
                      class="h-auto w-full box-border px-3 lg:px-6 absolute top-0 left-0 aspect-[1/0.87]"
                    >
                      <img
                        :src="search_image"
                        alt="Become a member"
                        class="object-cover w-full h-full"
                      />
                    </Link>
                  </div>
                  <div class="px-3 lg:px-6 pt-0 lg:pt-6">
                    <h5 class="uppercase">Become a member</h5>
                  </div>
                </div>
              </div>
            </div>
            <!-- Desktop -->
            <div
              :class="mergedResults.length ? 'pb-28' : ''"
              class="w-full mt-8 lg:grid grid-cols-3 hidden"
            >
              <ReuseTemplate
                class=""
                v-if="state.suggested.news"
                :data="state.suggested.news"
              />
              <div class="" v-if="state.suggested.event">
                <EventCard
                  class="border-l border-l-brand-dark-blue"
                  :event="state.suggested.event"
                />
                <div class="px-6 pt-3 lg:pt-6">
                  <h5 class="uppercase">Latest Events</h5>
                </div>
              </div>
              <div class="">
                <div
                  class="group relative box-border before:content-[''] before:block before:pt-[87%] w-full mb-4 last:mb-0 pb-4 md:mb-4 md:pb-0 lg:mb-0 border-l border-l-brand-dark-blue"
                >
                  <Link
                    :href="membership_page"
                    class="h-auto w-full box-border px-3 lg:px-6 absolute top-0 left-0 aspect-[1/0.87]"
                  >
                    <img
                      :src="search_image"
                      alt="Become a member"
                      class="object-cover w-full h-full"
                    />
                  </Link>
                </div>
                <div class="px-6 pt-3 lg:pt-6">
                  <h5 class="uppercase">Become a member</h5>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<style lang="postcss" scoped>
.result-item {
  @apply border-b border-b-brand-dark-blue pb-4 md:pb-0 md:border-b-0;
}

@screen md {
  .result-item:nth-child(2n) .event--card,
  .result-item:nth-child(2n) .news-a {
    @apply border-l border-l-brand-dark-blue;
  }
}
@screen lg {
  .result-item:nth-child(2n) .event--card,
  .result-item:nth-child(2n) .news-a {
    @apply border-r border-r-brand-dark-blue;
  }
}
.become-member {
  @apply border-l border-l-brand-dark-blue relative box-border w-full mb-0 pb-0;
  &:before {
    content: "";
    display: block;
    padding-top: 87%;
  }
}
.mobile-carousel {
  .inner-group {
    @apply pb-0 !important;
  }
  .inner-tittle {
    @apply px-3 pt-0 md:px-3 lg:pt-6 lg:px-6 !important;
  }
}
</style>
